import { formatDate as formatDateUtil } from 'src/app/utils';

interface FormattedCourse {
  isPdPlanRequest: true;
  name: string;
  id: number;
  courseId: number;
  courseItemId: number;
  selectedRequirementId: number;
  userId: number;
  time: string;
  sessionStartDate: Date;
  sessionEndDate: Date;

  dateOffered: string;
  location: string;
  room: string;
  parentRequirement: string;
  requirement: string;
  hours: string;
  openingBalance?: string;
  statusDisplay: string;
  statusKid: string;
  validationKid: string;
  validationDisplay: string;
  timeExported?: string;
  isMoecsExported: boolean;
}

/** Format session time from UTC for display. Pass entity timezone (e.g. getLocalTimezoneWithFallback()). */
function formatSessionTimeRange(
  sessionStartDate: any,
  sessionEndDate: any,
  timezone: string,
): string {
  const startDate = formatDateUtil.parseSessionTimeAsUtc(sessionStartDate);
  const endDate = formatDateUtil.parseSessionTimeAsUtc(sessionEndDate);
  const startTime = startDate ? formatDateUtil.asTimeWithTimezone(startDate, timezone) : '';
  const endTime = endDate ? formatDateUtil.asTimeWithTimezone(endDate, timezone) : '';
  return startTime && endTime ? `${startTime} - ${endTime}` : '';
}

/**
 * Format a PD plan course for display. Session times are stored as UTC; pass timezone so they display in entity/local time.
 * @param course - Raw course (sessionStartDate/sessionEndDate on course or course.CourseItem)
 * @param timezone - Entity timezone (e.g. EntityService.getLocalTimezoneWithFallback())
 */
const formatCourse = (course: any, timezone: string) => {
  const copy = { ...course };
  const sessionStart = copy?.sessionStartDate ?? copy?.CourseItem?.sessionStartDate;
  const sessionEnd = copy?.sessionEndDate ?? copy?.CourseItem?.sessionEndDate;
  const dateOffered = copy?.dateOffered ?? copy?.CourseItem?.dateOffered;
  const type = copy?.preRecordedOption ?? copy?.CourseItem?.preRecordedOption;

  const courseDisplay: FormattedCourse = {
    isPdPlanRequest: true,
    id: copy?.id,
    name: copy?.courseName,
    courseId: copy?.courseId,
    courseItemId: copy?.courseItemId,
    userId: copy?.userId,

    dateOffered: dateOffered ? formatDateUtil.asDateLocal(dateOffered) : '',
    time: formatSessionTimeRange(sessionStart, sessionEnd, timezone),
    sessionStartDate: copy?.sessionStartDate,
    sessionEndDate: copy?.sessionEndDate,

    location: copy?.location,
    room: copy?.roomNumber || '-',

    selectedRequirementId: Number(copy?.requirementId),
    parentRequirement: copy.topMostParentRequirementName,
    requirement: copy?.requirement,

    hours: copy?.adjustedValue || copy?.Course?.trackingValue,
    openingBalance: copy?.openingBalance || '0',

    statusKid: copy?.currentStatusKid,
    statusDisplay: copy.userCourseStatus,
    validationKid: copy?.validationKid,
    validationDisplay: copy?.validation,
    timeExported: copy?.timeExported,
    isMoecsExported: copy?.isMoecsExported || !!copy?.timeExported,
  };

  const isAsync = type === 'ASYNC';
  const isAnytime = type === 'ANYTIME';
  const isVirtual = copy?.virtualFlag == 1;

  if (isAsync) {
    courseDisplay.time = 'Async';
    courseDisplay.dateOffered = 'Async';
  }
  if (isAnytime) {
    courseDisplay.time = 'Anytime';
  }

  if (isVirtual) {
    courseDisplay.location = 'Virtual';
  }

  if (!courseDisplay.requirement) {
    courseDisplay.requirement = 'Portfolio/Unassigned';
  }

  return courseDisplay;
};

export { formatCourse, FormattedCourse };
